import { z } from "@/zod";
import { ulid, dateTime, postalCode, apartmentNumber } from "./commonFields";
import { AGREEMENT_STATUS } from "@/types/agreement";
import { format } from "date-fns";
import { DEFAULT_DATE_FORMAT } from "@/constants";
import i18n from "@/i18n";
import {
  questionnaireDataBase,
  questionnaireStatementsBase,
  questionnaireAttachments,
  fileMetadata,
} from "./questionnaire";

export const agreement = z.object({
  id: ulid,
  agreement_ref_number: z.number(),
  contractor_user_ref_number: z.string().nullable().optional(),
  contractor_full_name: z.string(),
  contractor_last_name: z.string().nullable().optional(),
  contractor_first_name: z.string().nullable().optional(),
  agreement_type_name: z.string(),
  agreement_company_name: z.string(),
  contractor_phone_number: z.string(),
  contractor_email: z.string(),
  contractor_personal_id_number: z.string(),
  agreement_status: z.enum(AGREEMENT_STATUS),
  statements_up_to_date: z.boolean().nullable().optional(),
  statements_confirmed_by_contractor_at: dateTime.nullable().optional(),
  contractor_registered_by: ulid.nullable().optional(),
  contractor_registered_by_name: z.string().nullable().optional(),
  agreement_start_date: z.string().date(),
  agreement_sign_date: z.string().date().nullable().optional(),
  agreement_end_date: z.string().date().nullable().optional(),
  agreement_ended_at: dateTime.nullable().optional(),
  city: z.string().nullable().optional(),
  postal_code: z.string().nullable().optional(),
  post_office: z.string().nullable().optional(),
  street: z.string().nullable().optional(),
  house_number: z.string().nullable().optional(),
  apartment_number: z.string().nullable().optional(),
  province_name: z.string().nullable().optional(),
  province_nfz_code: z.string().nullable().optional(),
  county: z.string().nullable().optional(),
  municipality: z.string().nullable().optional(),
  birth_date: z.string().date().nullable().optional(),
  birth_place: z.string().nullable().optional(),
  gender: z.string().nullable().optional(),
  bank_account_number: z.string().nullable().optional(),
  tax_office_name: z.string().nullable().optional(),
  citizenship_name: z.string().nullable().optional(),
  disability_certificate_code: z.string().nullable().optional(),
  rodo_accepted: z.boolean().nullable().optional(),
  regulations_accepted: z.boolean().nullable().optional(),
  is_student: z.boolean().nullable().optional(),
  school_name: z.string().nullable().optional(),
  student_id_number: z.string().nullable().optional(),
  student_id_valid_until: z.string().date().nullable().optional(),
  is_employed: z.boolean().nullable().optional(),
  employer_name: z.string().nullable().optional(),
  employment_certificate_issue_date: z.string().date().nullable().optional(),
  voluntary_sickness_insurance: z.boolean().nullable().optional(),
  is_pensioner: z.boolean().nullable().optional(),
  pension_number: z.string().nullable().optional(),
  pension_start_date: z.string().date().nullable().optional(),
  pension_office_name: z.string().nullable().optional(),
  is_disability_pensioner: z.boolean().nullable().optional(),
  disability_pension_number: z.string().nullable().optional(),
  disability_pension_start_date: z.string().date().nullable().optional(),
  disability_pension_office_name: z.string().nullable().optional(),
  disability_pension_type: z.string().nullable().optional(),
  has_valid_visa: z.boolean().nullable().optional(),
  visa_number: z.string().nullable().optional(),
  visa_valid_until: z.string().date().nullable().optional(),
  has_polish_card: z.boolean().nullable().optional(),
  polish_card_valid_until: z.string().date().nullable().optional(),
  has_residence_card: z.boolean().nullable().optional(),
  residence_card_valid_until: z.string().date().nullable().optional(),
  watched_instructional_videos: z.boolean().nullable().optional(),
  is_b2b_agreement: z.boolean().nullable().optional(),
  agreement_tax_status: z.string().nullable().optional(),
  recruitment_responsible_user_full_name: z.string().nullable().optional(),
  hr_department_responsible_user_full_name: z.string().nullable().optional(),
  hr_paper_confirmed: z.boolean().nullable().optional(),
  recruiter_paper_confirmed: z.boolean().nullable().optional(),
  deleted_at: dateTime.nullable().optional(),
  payroll_comment: z.string().nullable().optional(),
});

export const readAgreement = agreement.readonly();

const agreementFormBase = z.object({
  type_id: ulid,
  contractor_id: ulid,
  start_date: z.string().date(),
  agreementType: z
    .object({
      valid_from: z.string().optional(),
      valid_to: z.string().optional(),
    })
    .optional(),
});

export const agreementFormSchema = agreementFormBase.superRefine(
  (data, ctx) => {
    if (data.agreementType && data.start_date) {
      const startDate = new Date(data.start_date);
      const validFrom = data.agreementType.valid_from
        ? new Date(data.agreementType.valid_from)
        : null;
      const validTo = data.agreementType.valid_to
        ? new Date(data.agreementType.valid_to)
        : null;

      if (validFrom && startDate < validFrom) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t("agreements.errors.startDateBeforeValidFrom", {
            validFrom: format(validFrom, DEFAULT_DATE_FORMAT),
          }),
          path: ["start_date"],
        });
      }

      if (validTo && startDate > validTo) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t("agreements.errors.startDateAfterValidTo", {
            validTo: format(validTo, DEFAULT_DATE_FORMAT),
          }),
          path: ["start_date"],
        });
      }
    }
  },
);

export const writeAgreement = agreementFormBase.pick({
  type_id: true,
  contractor_id: true,
  start_date: true,
});

// Tax status changed info schema
const taxStatusChangedInfo = z.object({
  parallel_agreement_id: ulid,
  parallel_agreement_ref_number: z.number(),
  parallel_agreement_tax_status: z.string(),
  new_tax_status: z.string(),
});

// B2B data schema
const b2bQuestionnaireBase = z.object({
  company_tax_identification_number: z.string().nullable(),
  company_name: z.string().nullable(),
  company_postal_code: postalCode.nullable(),
  company_city: z.string().nullable(),
  company_street_name: z.string().nullable(),
  company_street_number: z.string().nullable(),
  company_apartment_number: apartmentNumber.nullish(),
});

export const agreementDetail = z.object({
  agreement_id: ulid,
  agreement_ref_number: z.number(),
  agreement_status: z.enum(AGREEMENT_STATUS),
  agreement_start_date: z.string().date(),
  agreement_sign_date: z.string().date().nullable(),
  agreement_end_date: z.string().date().nullable(),
  agreement_ended_at: dateTime.nullable().optional(),
  agreement_accepted_by_contractor_at: dateTime.nullable(),
  agreement_type_id: ulid,
  agreement_type_name: z.string(),
  agreement_company_id: ulid,
  agreement_company_name: z.string(),
  agreement_company_full_name: z.string(),
  agreement_last_amendment_number: z.number(),
  agreement_last_amendment_date: z.string().date().nullable(),
  agreement_template_code: z.string(),
  questionnaire_id: ulid,
  questionnaire_is_draft: z.boolean(),
  agreement_statements_confirmed_by_contractor_at: dateTime.nullable(),
  agreement_statements_up_to_date: z.boolean().nullable(),
  is_b2b_agreement: z.boolean(),
  agreement_tax_status: z.string().nullable().optional(),
  recruitment_responsible_user_full_name: z.string().nullable().optional(),
  hr_department_responsible_user_full_name: z.string().nullable().optional(),
  agreement_hr_paper_confirmed: z.boolean().nullable().optional(),
  agreement_hr_paper_confirmed_at: dateTime.nullable().optional(),
  agreement_hr_paper_confirmed_by_name: z.string().nullable().optional(),
  agreement_recruiter_paper_confirmed: z.boolean().nullable().optional(),
  agreement_recruiter_paper_confirmed_at: dateTime.nullable().optional(),
  agreement_recruiter_paper_confirmed_by_name: z.string().nullable().optional(),
});

export const contractorDetail = z.object({
  contractor_id: ulid,
  contractor_user_id: ulid,
  contractor_user_ref_number: z.string(),
  contractor_full_name: z.string(),
  contractor_last_name: z.string().nullable().optional(),
  contractor_first_name: z.string().nullable().optional(),
  contractor_email: z.string(),
  contractor_phone_number: z.string(),
  contractor_personal_id_number: z.string().nullable().optional(),
  contractor_registered_by: ulid.nullable().optional(),
  contractor_registered_by_name: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  postal_code: z.string().nullable().optional(),
  post_office: z.string().nullable().optional(),
  street: z.string().nullable().optional(),
  house_number: z.string().nullable().optional(),
  apartment_number: z.string().nullable().optional(),
  province_name: z.string().nullable().optional(),
  province_nfz_code: z.string().nullable().optional(),
  county: z.string().nullable().optional(),
  municipality: z.string().nullable().optional(),
  birth_date: z.string().date().nullable().optional(),
  birth_place: z.string().nullable().optional(),
  gender: z.string().nullable().optional(),
  bank_account_number: z.string().nullable().optional(),
  tax_office_name: z.string().nullable().optional(),
  citizenship_name: z.string().nullable().optional(),
  disability_certificate_code: z.string().nullable().optional(),
  rodo_accepted: z.boolean().nullable().optional(),
  regulations_accepted: z.boolean().nullable().optional(),
  is_student: z.boolean().nullable().optional(),
  school_name: z.string().nullable().optional(),
  student_id_number: z.string().nullable().optional(),
  student_id_valid_until: z.string().date().nullable().optional(),
  is_employed: z.boolean().nullable().optional(),
  employer_name: z.string().nullable().optional(),
  employment_certificate_issue_date: z.string().date().nullable().optional(),
  voluntary_sickness_insurance: z.boolean().nullable().optional(),
  is_pensioner: z.boolean().nullable().optional(),
  pension_number: z.string().nullable().optional(),
  pension_start_date: z.string().date().nullable().optional(),
  pension_office_name: z.string().nullable().optional(),
  is_disability_pensioner: z.boolean().nullable().optional(),
  disability_pension_number: z.string().nullable().optional(),
  disability_pension_start_date: z.string().date().nullable().optional(),
  disability_pension_office_name: z.string().nullable().optional(),
  disability_pension_type: z.string().nullable().optional(),
  has_valid_visa: z.boolean().nullable().optional(),
  visa_number: z.string().nullable().optional(),
  visa_valid_until: z.string().date().nullable().optional(),
  has_polish_card: z.boolean().nullable().optional(),
  polish_card_valid_until: z.string().date().nullable().optional(),
  has_residence_card: z.boolean().nullable().optional(),
  residence_card_valid_until: z.string().date().nullable().optional(),
  watched_instructional_videos: z.boolean().nullable().optional(),
});

const agreementComment = z.object({
  id: ulid,
  comment: z.string(),
  created_at: dateTime,
  created_by: ulid,
  created_by_name: z.string(),
  created_by_role_name: z.string(),
});

const agreementHistoryEntry = z.object({
  action: z.string(),
  user_id: ulid,
  user_full_name: z.string(),
  user_role_name: z.string(),
  updated_at: dateTime,
});

/** Stany skanu widoczne w szczegółach umowy (ASMMNT-83, etap 2); `pending_upload` backend pomija. */
export const AGREEMENT_PAPER_SCAN_STATES = ["active", "deleted"] as const;

/** Skan lub zdjęcie podpisanej umowy papierowej z sekcji „Załączniki". */
export const agreementPaperScan = z.object({
  id: ulid,
  path: z.string(),
  filename: z.string(),
  /**
   * Rozmiar sformatowany przez backend (`format_size`, jak w `agreement_files`). `null` po usunięciu,
   * ale też dla aktywnego skanu, którego obiektu brak w listingu S3 — konsument potrzebuje fallbacku.
   */
  size: z.string().nullable(),
  state: z.enum(AGREEMENT_PAPER_SCAN_STATES),
  created_by: ulid,
  /**
   * `CONCAT_WS` nazwiska i imienia z JOIN na `asm.users` — pusty string tylko przy braku imienia
   * i nazwiska; konsument potrzebuje fallbacku.
   */
  created_by_name: z.string(),
  uploaded_at: dateTime,
  deleted_at: dateTime.nullable(),
  deleted_by: ulid.nullable(),
  /**
   * `NULLIF(CONCAT_WS(...), '')` — `null` także przy `state: "deleted"`, gdy usuwający nie ma
   * imienia i nazwiska; konsument potrzebuje fallbacku.
   */
  deleted_by_name: z.string().nullable(),
});

export type AgreementPaperScan = z.infer<typeof agreementPaperScan>;

/** Tryby pobrania pliku umowy rejestrowane przez backend. */
export const AGREEMENT_FILE_DOWNLOAD_MODES = ["download", "preview"] as const;

/** Zdarzenie pobrania albo podglądu pliku umowy (PDF wygenerowany lub skan). */
export const agreementFileDownload = z.object({
  id: ulid,
  path: z.string(),
  filename: z.string(),
  mode: z.enum(AGREEMENT_FILE_DOWNLOAD_MODES),
  downloaded_by: ulid,
  /** `CONCAT_WS` nazwiska i imienia z JOIN na `asm.users` — pusty string tylko przy braku imienia i nazwiska. */
  downloaded_by_name: z.string(),
  /** Nazwa roli systemowej użytkownika (INNER JOIN, więc w praktyce zawsze obecna; `nullable` tylko dla odporności schematu). */
  downloaded_by_role_name: z.string().nullable(),
  downloaded_at: dateTime,
});

export type AgreementFileDownload = z.infer<typeof agreementFileDownload>;
export type AgreementFileDownloadMode = AgreementFileDownload["mode"];

/**
 * Agregat pobrań per plik (ASMMNT-83, etap 2): `file_downloads` w szczegółach to tylko najnowsze
 * zdarzenia umowy, więc „łącznie N" i czas ostatniego dostępu liczy baza.
 */
export const agreementFileDownloadTotal = z.object({
  path: z.string(),
  total: z.number().int().nonnegative(),
  last_downloaded_at: dateTime,
});

export type AgreementFileDownloadTotal = z.infer<
  typeof agreementFileDownloadTotal
>;

export const agreementDetailResponse = z.object({
  agreement: agreementDetail,
  contractor: contractorDetail,
  draft: questionnaireDataBase,
  non_draft: questionnaireDataBase,
  draft_statements: questionnaireStatementsBase,
  statements: questionnaireStatementsBase,
  draft_b2b: b2bQuestionnaireBase,
  b2b: b2bQuestionnaireBase,
  questionnaire_attachments: questionnaireAttachments,
  agreement_files: z.array(fileMetadata),
  comments: z.array(agreementComment),
  allowed_new_statuses: z.array(z.string()),
  can_send_questionnaire_link: z.boolean(),
  tax_status_changed_info: taxStatusChangedInfo.nullable(),
  history: z.array(agreementHistoryEntry),
  /**
   * Skany umowy papierowej (ASMMNT-83, etap 2). `.optional()`, a nie `.default([])`: szczegóły nie są
   * parsowane zodem w runtime (`fetchAgreement` zwraca `response.data` bez `.parse`), więc `.default`
   * tylko ukrywałby `undefined` przed kompilatorem. `.optional()` wymusza `?? []` u konsumentów —
   * ważne, gdy portal jest wdrożony przed backendem etapu 2 (pola wtedy nie przychodzą).
   */
  paper_scans: z.array(agreementPaperScan).optional(),
  /** Zdarzenia pobrań i podglądów plików umowy (ASMMNT-83, etap 2); brak pola = starszy backend, patrz `paper_scans`. */
  file_downloads: z.array(agreementFileDownload).optional(),
  /** Agregat per plik do `file_downloads` (lista przycięta do najnowszych zdarzeń); brak = starszy backend. */
  file_download_totals: z.array(agreementFileDownloadTotal).optional(),
});

export const readAgreementDetail = agreementDetailResponse.readonly();

export type ReadAgreementDetail = z.infer<typeof readAgreementDetail>;
export type TaxStatusChangedInfo = z.infer<typeof taxStatusChangedInfo>;

/**
 * Odpowiedź PATCH /agreements/{id}/paper-confirmation/{kind} (ASMMNT-83) —
 * walidowana zanim trafi do cache szczegółów.
 */
export const agreementPaperConfirmationFields = z.object({
  agreement_hr_paper_confirmed: z.boolean().nullable(),
  agreement_hr_paper_confirmed_at: dateTime.nullable(),
  agreement_hr_paper_confirmed_by: z.string().nullable(),
  agreement_hr_paper_confirmed_by_name: z.string().nullable(),
  agreement_recruiter_paper_confirmed: z.boolean().nullable(),
  agreement_recruiter_paper_confirmed_at: dateTime.nullable(),
  agreement_recruiter_paper_confirmed_by: z.string().nullable(),
  agreement_recruiter_paper_confirmed_by_name: z.string().nullable(),
});

export type AgreementPaperConfirmationFields = z.infer<
  typeof agreementPaperConfirmationFields
>;

/** `data` odpowiedzi `POST …/files/download-url`: presigned GET (300 s) i zapisane zdarzenie. */
export const agreementFileUrlResponse = z.object({
  // Presigned URL z boto3 jest zawsze https; `.url()` samo przepuszcza też np. `javascript:`,
  // a adres trafia do przeglądarki (pobranie/podgląd), więc https wymuszamy jawnie.
  url: z.string().url().startsWith("https://"),
  expires_in: z.number().int().positive(),
  event: agreementFileDownload,
});

export type AgreementFileUrlResponse = z.infer<typeof agreementFileUrlResponse>;

/** `data` odpowiedzi `POST …/paper-scans/complete` i `…/complete-delete`. */
export const agreementPaperScanMutationResponse = z.object({
  scan: agreementPaperScan,
  agreement: agreementPaperConfirmationFields,
});

export type AgreementPaperScanMutationResponse = z.infer<
  typeof agreementPaperScanMutationResponse
>;

/**
 * Pełne body tych odpowiedzi — `completeAttachment` z `lib/file-storage` zwraca surowy JSON
 * (`{ ok, data }`), a nie samo `data` jak `APIService`.
 */
export const agreementPaperScanCompletionBody = z.object({
  ok: z.string().optional(),
  data: agreementPaperScanMutationResponse,
});
